import { FC, useCallback, useMemo } from "react";

import { convertStringToDate, formattedDateRange, formattedYearRange } from "src/js/util/dates";

import { FilterDateRange } from "./FilterDates";
import { FilterFloorArea } from "./FilterFloorArea";
import { FilterNewBuilds } from "./FilterNewBuilds";
import { FilterPropertyTypes } from "./FilterPropertyTypes";
import { FilterTenure } from "./FilterTenure";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { FilterBedrooms } from "./FilterBedrooms";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { hiddenFieldsByFeature } from "../../util/hiddenFields";
import { isZoopla } from "../../util/isZoopla";
import { LegacyComparablesFilterOptions as ComparablesFilterOptions } from "../../typings/Comparables";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import { DisabledMessage, FiltersProps } from "./Filters";
import { possibleUKCombinations, possibleUSCombinations } from "../../util/PropertyTypes";
import { PropertyDesignationGQL } from "react-migration/lib/typings/comparables/Comparables";
import { TenureTypes } from "../../typings/Tenure";

export type LegacyComparablesFilterFields =
  | "dateFrom"
  | "dateTo"
  | "dcsConditions"
  | "dcsConditions.Commercial"
  | "dcsConditions.Other"
  | "dcsConditions.Residential"
  | "newBuildsOnly"
  | "tenure"
  | "floorArea"
  | "bedrooms"
  | "yearBuiltFrom"
  | "yearBuiltTo";

export const LegacyFilters: FC<FiltersProps<LegacyComparablesFilterFields, ComparablesFilterOptions>> = ({
  disabled,
  disabledFields,
  priceMode,
  filters,
  updateFilters,
  selectedDCSConditions,
  unitPreference,
  theme,
  hiddenFields,
}) => {
  const { t } = useTranslation();

  const startDate = filters.dateFrom === null ? null : convertStringToDate(filters.dateFrom);
  const setStartDate = (date?: Date | null) => updateFilters({ dateFrom: date ? formattedDateRange(date) : null });

  const endDate = filters.dateTo === null ? null : convertStringToDate(filters.dateTo);
  const setEndDate = (date?: Date | null) => updateFilters({ dateTo: date ? formattedDateRange(date) : null });

  const setDCSConditions = useCallback(
    (dcsConditions: DCSCondition[]) => {
      console.log("setting dcs conditions", dcsConditions);
      updateFilters({ dcsConditions });
    },
    [updateFilters]
  );
  const setShowNewBuildOnly = useCallback(
    (newBuildsOnly: boolean) => updateFilters({ newBuildsOnly }),
    [updateFilters]
  );
  const setTenureType = useCallback(
    (tenure: ComparablesFilterOptions["tenure"]) => updateFilters({ tenure }),
    [updateFilters]
  );
  const [yearBuiltFrom, setYearBuiltFrom] = useMemo(
    () => [
      filters.yearBuiltFrom ? convertStringToDate(filters.yearBuiltFrom.toString()) : undefined,
      (date?: Date | null) =>
        updateFilters({
          yearBuiltFrom: date ? formattedYearRange(date) : undefined,
        }),
    ],
    [filters, updateFilters]
  );
  const [yearBuiltTo, setYearBuiltTo] = useMemo(
    () => [
      filters.yearBuiltTo ? convertStringToDate(filters.yearBuiltTo.toString()) : undefined,
      (date?: Date | null) => updateFilters({ yearBuiltTo: date ? formattedYearRange(date) : undefined }),
    ],
    [filters, updateFilters]
  );
  const setAreaMin = useCallback(
    (min: number) => {
      updateFilters({
        floorArea: {
          min: isNaN(Number(min)) || Number(min) === 0 ? undefined : (min as number),
          max: filters.floorArea?.max,
        },
      });
    },
    [filters, updateFilters]
  );
  const setAreaMax = useCallback(
    (max: number) => {
      updateFilters({
        floorArea: {
          min: filters.floorArea?.min,
          max: isNaN(Number(max)) ? undefined : (max as number),
        },
      });
    },
    [filters, updateFilters]
  );
  const setBedroomsMin = useCallback(
    (min: number) => {
      updateFilters({
        bedrooms: {
          min: isNaN(min) ? undefined : min,
          max: filters.bedrooms?.max,
        },
      });
    },
    [filters, updateFilters]
  );

  const setBedroomsMax = useCallback(
    (max: number) => {
      updateFilters({
        bedrooms: {
          min: filters.bedrooms?.min,
          max: isNaN(max) ? undefined : max,
        },
      });
    },
    [filters, updateFilters]
  );

  // there are 2 ways to hide fields.
  // * pass in hiddenFields - for settings users can change in the app or different display requirements for different areas of the app e.g. comparables within the site card has limited fields
  // * via features. These don't change during the course of a user interacting with the app and so are hardcoded in to this field
  // * via selected price mode - some fields are hidden based on the price mode selected e.g. year built filter is only present for zoopla
  const computedHiddenFields = useMemo(
    () =>
      hiddenFieldsByFeature.reduce((set, [hideCheck, fields]) => {
        const f = fields as Set<LegacyComparablesFilterFields>;
        if (hideCheck()) {
          return new Set([...set, ...f]);
        }
        return set;
      }, hiddenFields || new Set<LegacyComparablesFilterFields>()),
    [hiddenFields]
  );

  const usAccess = hasFeature(Feature.usAccess);
  const options = usAccess ? possibleUSCombinations : possibleUKCombinations;

  return (
    <div className="px-2 pb-2 flex flex-col text-xs md:text-sm">
      <DisabledMessage enableTooltip={disabledFields?.has("dcsConditions")}>
        <FilterPropertyTypes
          options={options}
          disabledFields={
            new Set(
              [...(disabledFields ?? [])]
                .filter((a) => /\.(Commercial|Residential|Other)/.test(a))
                .map((a) => a.replace(/^dcsConditions\./, "") as PropertyDesignationGQL)
            )
          }
          hiddenFields={
            new Set(
              [...(computedHiddenFields ?? [])]
                .filter((a) => /\.(Commercial|Residential|Other)/.test(a))
                .map((a) => a.replace(/^dcsConditions\./, "") as PropertyDesignationGQL)
            )
          }
          disabled={disabled || disabledFields?.has("dcsConditions")}
          selectedPropTypes={selectedDCSConditions}
          setSelectedPropTypes={setDCSConditions}
          theme={theme}
        />
      </DisabledMessage>
      {computedHiddenFields?.has("dateFrom") && computedHiddenFields?.has("dateTo") ? null : (
        <DisabledMessage enableTooltip={disabledFields?.has("dateFrom") && disabledFields?.has("dateTo")}>
          <FilterDateRange
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            disabled={disabled || (disabledFields?.has("dateFrom") && disabledFields?.has("dateTo"))}
            label={
              isZoopla(priceMode)
                ? t("comparables.map.sidebar.filters.filters.zoopla_date")
                : t("comparables.map.sidebar.filters.filters.date")
            }
          />
        </DisabledMessage>
      )}
      {computedHiddenFields?.has("tenure") ? null : (
        <DisabledMessage enableTooltip={disabledFields?.has("tenure")}>
          <FilterTenure
            disabled={disabled || disabledFields?.has("tenure")}
            tenureTypes={
              filters.tenure === "Any"
                ? [TenureTypes.FREEHOLD, TenureTypes.LEASEHOLD]
                : filters.tenure?.length
                ? [filters.tenure]
                : []
            }
            setTenureTypes={(tenure) => {
              if (tenure.length === 2) {
                setTenureType("Any");
              } else if (tenure.length === 0) {
                // Initially this would swap them over
                if (filters.tenure === "F") {
                  setTenureType("L");
                } else {
                  setTenureType("F");
                }
              } else {
                setTenureType(tenure[0]);
              }
            }}
            availableTenures={[TenureTypes.FREEHOLD, TenureTypes.LEASEHOLD]}
            theme={theme}
          />
        </DisabledMessage>
      )}
      {computedHiddenFields?.has("floorArea") ? null : (
        <DisabledMessage enableTooltip={disabledFields?.has("floorArea")}>
          <FilterFloorArea
            disabled={disabled || disabledFields?.has("floorArea")}
            setAreaMin={setAreaMin}
            areaMin={filters.floorArea?.min}
            areaMax={filters.floorArea?.max}
            setAreaMax={setAreaMax}
            unitPreference={unitPreference}
            theme={theme}
          />
        </DisabledMessage>
      )}
      {(computedHiddenFields?.has("yearBuiltFrom") && computedHiddenFields?.has("yearBuiltTo")) ||
      !isZoopla(priceMode) ? null : (
        <DisabledMessage
          description={t("comparables.map.sidebar.filters.built_filter_disabled_for_epc")}
          enableTooltip={disabledFields?.has("yearBuiltFrom") || disabledFields?.has("yearBuiltTo")}
        >
          <FilterDateRange
            disabled={disabled || (disabledFields?.has("yearBuiltFrom") && disabledFields?.has("yearBuiltTo"))}
            startDate={yearBuiltFrom}
            setStartDate={setYearBuiltFrom}
            endDate={yearBuiltTo}
            setEndDate={setYearBuiltTo}
            mode="year"
            label={t("comparables.map.sidebar.filters.filters.built")}
          />
        </DisabledMessage>
      )}
      {computedHiddenFields?.has("bedrooms") || !isZoopla(priceMode) ? null : (
        <DisabledMessage
          description={t("comparables.map.sidebar.filters.bedroom_filter_disabled_for_epc")}
          enableTooltip={disabledFields?.has("bedrooms")}
        >
          <FilterBedrooms
            disabled={disabled || disabledFields?.has("bedrooms")}
            min={
              disabled || disabledFields?.has("bedrooms") || filters.bedrooms?.min == null ? NaN : filters.bedrooms.min
            }
            setMin={setBedroomsMin}
            max={
              disabled || disabledFields?.has("bedrooms") || filters.bedrooms?.max == null ? NaN : filters.bedrooms.max
            }
            setMax={setBedroomsMax}
            theme={theme}
          />
        </DisabledMessage>
      )}
      {computedHiddenFields?.has("newBuildsOnly") ? null : (
        <DisabledMessage enableTooltip={disabledFields?.has("newBuildsOnly")}>
          <FilterNewBuilds
            disabled={disabled || disabledFields?.has("newBuildsOnly")}
            showNewBuildOnly={!!filters.newBuildsOnly}
            setShowNewBuildOnly={setShowNewBuildOnly}
            theme={theme}
          />
        </DisabledMessage>
      )}
    </div>
  );
};

LegacyFilters.displayName = "Filters";
